import React, { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Alert, Box, Chip, Grid, Typography } from '@mui/material';
import { Colors } from '@provider-portal/common/styles/bolt-uikit-colors';
import { commonStyles } from '@provider-portal/common/styles/commonStyles';
import { format } from 'date-fns';
import { ViewDetailsButton } from 'promotions/components/ActivePromotionsGrid/VideDetailsButtonComponent';
import { PromotionDetails } from 'promotions/types';

const styles = {
  card: {
    minWidth: '396px',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    background: Colors.neutral200,
    borderRadius: '16px',
    height: '100%',
    minHeight: '384px',
    boxSizing: 'border-box',
  },
  promotionDetailsItems: {
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  info: {
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    alignSelf: 'stretch',
    fontSize: '1.25rem',
  },
  titleText: {
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '3rem',
    gap: '100%',
  },
  statTitle: {
    color: Colors.neutral700,
  },
  noData: {
    color: Colors.neutral700,
  },
  chip: {
    ...commonStyles.TypographyLabel,
    color: Colors.purple500,
    backgroundColor: Colors.purple100,
  },
  banAlert: {
    ...commonStyles.TypographyHelperText,
    color: Colors.neutral900,
    borderRadius: '8px',
    backgroundColor: Colors.red100,
  },
  balAlertIcon: {
    color: Colors.red500,
  },
} as const;

export interface UpcomingPromotionsProps {
  strings: {
    promotionCard: {
      viewDetails: string;
      tooltipInfo: string;
    };
    upcomingPromotionsCard: {
      upcoming: string;
    };
  };
  promotionDetail: PromotionDetails;
  promotionClicked: (id: number) => void;
}

const BanAlert = (props: { bannedUntil: number }) => {
  const banDate = format(props.bannedUntil, 'MMM dd, yyyy');
  const intl = useIntl();
  const banMessage = intl.formatMessage(
    {
      id: 'components.activePromotionsGrid.upcomingPromotionsCard.banAlertMessage',
    },
    {
      banDate: <strong>{banDate}</strong>,
    },
  );
  return (
    <Alert
      severity="error"
      icon={<WarningRoundedIcon sx={styles.balAlertIcon} />}
      sx={styles.banAlert}
    >
      {banMessage}
    </Alert>
  );
};

export const UpcomingPromotion: React.FC<UpcomingPromotionsProps> = memo(
  ({ strings, promotionDetail, promotionClicked }) => {
    const intl = useIntl();

    const onClickHandler = useCallback(() => {
      promotionClicked(promotionDetail.id);
    }, [promotionDetail.id, promotionClicked]);

    const startDate = format(promotionDetail.startDate, 'MMM dd, yyyy');
    const endDate = format(promotionDetail.endDate, 'MMM dd, yyyy');
    return (
      <Box sx={styles.card}>
        <Box sx={styles.info}>
          <Box sx={styles.title}>
            <Typography variant="h6" sx={styles.titleText}>
              {promotionDetail.promotionName}
            </Typography>
          </Box>

          <Box>
            <Grid container direction="row" sx={styles.promotionDetailsItems}>
              <Grid item>
                <CalendarTodayOutlinedIcon />
              </Grid>
              <Grid item>
                <Grid container direction="column">
                  <Grid item>
                    <Typography variant="bodyS" sx={styles.statTitle}>
                      {intl.formatMessage({
                        id: 'components.activePromotionsGrid.statsTitles.duration',
                      })}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{`${startDate} - ${endDate}`}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Chip label={strings.upcomingPromotionsCard.upcoming} sx={styles.chip} />
              </Grid>
            </Grid>
            {promotionDetail.bannedUntil && <BanAlert bannedUntil={promotionDetail.bannedUntil} />}
          </Box>
        </Box>

        <ViewDetailsButton strings={strings.promotionCard} onClick={onClickHandler} />
      </Box>
    );
  },
);
